import { z } from 'zod';
import { getJson, sendJson } from '@/shared/api/client';
import { pageSchema } from '@/shared/api/page';

export const DRAFT_STATUSES = ['pending', 'approved', 'sending', 'sent', 'rejected'] as const;

const deliverySchema = z.object({
  id: z.string(),
  recipientName: z.string(),
  phoneMasked: z.string(),
  status: z.enum(['queued', 'sent', 'delivered', 'read', 'failed']),
  updatedAt: z.string(),
});
export type Delivery = z.infer<typeof deliverySchema>;

export const draftSchema = z.object({
  id: z.string(),
  source: z.enum(['answer', 'update']),
  channel: z.enum(['portal', 'teams']),
  status: z.enum(DRAFT_STATUSES),
  requester: z.object({ name: z.string(), role: z.string() }),
  createdAt: z.string(),
  originalText: z.string(),
  finalText: z.string(),
  disclaimer: z.string(),
  /** The final text exactly as recipients get it: WhatsApp formatting plus the disclaimer. */
  whatsappPreview: z.string().default(''),
  list: z.object({ id: z.string(), name: z.string(), count: z.number().int() }),
  sources: z.array(z.object({ documentId: z.string(), title: z.string(), publishDate: z.string(), page: z.number().int() })),
  decidedBy: z.string().nullable(),
  decidedAt: z.string().nullable(),
  rejectReason: z.string().nullable(),
  deliveries: z.array(deliverySchema),
});
export type Draft = z.infer<typeof draftSchema>;

export type DraftParams = { page: number; pageSize: number; status: string; q?: string };

const compact = (p: DraftParams) => Object.fromEntries(Object.entries(p).filter(([, v]) => v !== ''));

export const fetchDrafts = (params: DraftParams, signal?: AbortSignal) =>
  getJson('/drafts', pageSchema(draftSchema), { params: compact(params), ...(signal ? { signal } : {}) });

export const approveDraft = (id: string, body: { text: string; listId: string }) =>
  sendJson('post', `/drafts/${id}/approve`, draftSchema, body);

export const rejectDraft = (id: string, body: { reason: string }) => sendJson('post', `/drafts/${id}/reject`, draftSchema, body);

/** Formatted by the same code that sends, so the preview is exactly what recipients get. */
export const previewMessage = (text: string) => sendJson('post', '/drafts/preview', z.object({ text: z.string() }), { text });
